import { Stack } from "@mui/material";
import { useRef, useState } from "react";
import { useParseTL } from "../../hooks/useParseTL";
import {
  removeClassification,
  submitQClassification,
} from "../../services/qClassificationService";
import { SubmissionPayload } from "../../types/Inspection";
import { ModuleSize } from "../../types/ModuleSize";
import { PalettesProps } from "../../types/palettes";
import {
  handleModuleSearch,
  handleOrderSearch,
  ModuleData,
  ProductData,
} from "./handlers";
import ClassificationForm, {
  ClassificationMessageRef,
  QClassificationPayload,
} from "./sections/ClassificationForm";
import { openOrUpdateImageWindow } from "./sections/ELImageViewer";
import { ELInspectionForm } from "./sections/ELInspectionForm";
import FlasherAccordion from "./sections/FlasherAccordion";
import ModuleSearchForm, {
  ModuleSearchFormRef,
} from "./sections/ModuleSearchForm";
import ModuleSizeSelection from "./sections/ModuleSizeSelection";
import OrderForm from "./sections/OrderForm";
import Palettes from "./sections/Palettes";
import TLFileInput from "./sections/TLFileInput";
import { VisualInspectionForm } from "./sections/VisualInspectionForm";
import { generateIVData } from "../../utils/generateIVData";
import ModuleSearchBySerialNumber, {
  ModuleSearchBySerialNumberRef,
} from "./sections/ModuleSearchBySerialNumber";

function QClassification() {
  const [paletteData, setPaletteData] = useState<PalettesProps["palettes"]>({});
  const [productData, setProductData] = useState<ProductData | null>(null);
  const [moduleData, setModuledata] = useState<ModuleData | null>(null);
  const [imageData, setImageData] = useState<string | null>(null);
  const [isValidModule, setIsValidModule] = useState<boolean>(false);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<
    string | undefined
  >(undefined);
  const [selectedTrackingNumber, setSelectedTrackingNumber] = useState<
    number | undefined
  >(undefined);
  const [moduleSize, setModuleSize] = useState<ModuleSize>("S");
  const [visualInspection, setVisualInspection] = useState<SubmissionPayload>();
  const [elInspection, setELInspection] = useState<SubmissionPayload>();
  const [rawTlXml, setRawTlXml] = useState<string | null>(null);
  const [articleData, setArticleData] = useState<any | null>(null);

  const moduleSearchRef = useRef<ModuleSearchFormRef>(null);
  const moduleSearchBySerialNumberRef =
    useRef<ModuleSearchBySerialNumberRef>(null);
  const classificationMessageRef = useRef<ClassificationMessageRef>(null);

  const { printLabel, svgElement, parseTL } = useParseTL();

  const handleShowImage = () => {
    imageData && moduleData && openOrUpdateImageWindow(imageData, moduleData);
  };

  const handleResetFull = () => {
    setSelectedTrackingNumber(undefined);
    moduleSearchRef.current?.reset();
    moduleSearchBySerialNumberRef.current?.reset();
    classificationMessageRef.current?.reset();
    setModuledata(null);
    setImageData(null);
    setIsValidModule(false);
    setVisualInspection(undefined);
    setELInspection(undefined);
  };

  const handleSubmitClassification = async (data: QClassificationPayload) => {
    if (!rawTlXml) {
      alert("First load a Label Tample (.tl File).");
      return;
    }

    if (!moduleData) {
      alert("No Module Data found");
      return;
    }

    try {
      const labelData: Record<string, string> = {
        ProductFamily: "MegaSlate®",
        ModuleType: "MegaSlate® II Black 6DM145",
        Version: "15",
        ArticleNumber: data.articleNumber || "",
        PNominal: "145 Wp",
        Pmeas: "",
        Isc: "9.44 A",
        UoC: "20.3 V",
        MaxPPCurrent: "8.9 A",
        MaxPPVoltage: "16.7 V",
        MaxSytemVoltage: "1000 volt DC",
        MaxOCP: "18 A",
        ProductId: moduleData.serialNumber || "undefined",
        ToleranceText1: "Tolerance for Pmax is +/- 3% and for Isc is +/- 3%",
        ToleranceText2:
          "Electrical rating at Standard Test Conditions (1000 W/m², 25 °C, AM 1.5 according to EN 60904-3)",
      };

      await submitQClassification(data);

      if (!["C", "Q"].includes(data.classification)) {
        await printLabel(labelData);
      }

      await handleOrderSearch(
        selectedOrderNumber!,
        setPaletteData,
        setProductData,
        setModuleSize
      );
    } catch (error: any) {
      console.error("Erro ao enviar classificação ou imprimir:", error);
      alert("Erro ao salvar ou imprimir: " + error.message);
    }
  };

  const handlePaletteEdition = async (updatedOrders: string[]) => {
    await removeClassification(updatedOrders);
    await handleOrderSearch(
      selectedOrderNumber!,
      setPaletteData,
      setProductData,
      setModuleSize
    );
  };

  return (
    <Stack direction="column" spacing={3} pt={3}>
      <ModuleSearchBySerialNumber
        ref={moduleSearchBySerialNumberRef}
        onSubmit={({ serialNumber }) => {
          handleModuleSearch(
            setModuledata,
            setImageData,
            undefined,
            serialNumber.toString()
          );
          setSelectedTrackingNumber(serialNumber);
          classificationMessageRef.current?.reset();
        }}
        onShowImageClick={handleShowImage}
        showImageEnabled={!!imageData}
      />

      <TLFileInput
        onFileLoaded={setRawTlXml}
        svgElement={svgElement}
        parseTL={parseTL}
      />

      <OrderForm
        onSubmit={({ orderNumber }) => {
          handleOrderSearch(
            orderNumber,
            setPaletteData,
            setProductData,
            setModuleSize
          );
          setSelectedOrderNumber(orderNumber);
          handleResetFull();
        }}
        svgElement={svgElement}
      />

      <ModuleSearchForm
        ref={moduleSearchRef}
        onSubmit={({ trackingNumber }) => {
          handleModuleSearch(
            setModuledata,
            setImageData,
            trackingNumber,
            undefined
          );
          setSelectedTrackingNumber(trackingNumber);
          classificationMessageRef.current?.reset();
        }}
        onShowImageClick={handleShowImage}
        showImageEnabled={!!imageData}
      />

      <ModuleSizeSelection
        value={moduleSize}
        onChange={(c) => setModuleSize(c)}
      />

      {moduleData && productData && (
        <FlasherAccordion
          moduleData={{ ...moduleData, ivCurve: generateIVData(50000) }}
          productData={productData}
          isModuleValid={setIsValidModule}
        />
      )}

      {moduleSize && moduleData && (
        <VisualInspectionForm
          visualInspection={moduleData.visualInspection}
          onSubmit={setVisualInspection}
          size={moduleSize}
        />
      )}
      {moduleSize && moduleData && (
        <ELInspectionForm
          elInspection={moduleData.elInspection}
          onSubmit={setELInspection}
          size={moduleSize}
        />
      )}

      <ClassificationForm
        ref={classificationMessageRef}
        onSubmit={(data) => {
          handleSubmitClassification(data);
        }}
        isValidModule={isValidModule}
        orderNumber={selectedOrderNumber}
        trackingNumber={selectedTrackingNumber}
        articles={{
          A: paletteData["A"]?.id,
          A2: paletteData["A2"]?.id,
          B: paletteData["B"]?.id,
          C: paletteData["C"]?.id,
          Q: paletteData["Q"]?.id,
        }}
        visualInspection={visualInspection}
        elInspection={elInspection}
      />

      <Palettes
        palettes={paletteData}
        onEditSave={(updatedOrders) => handlePaletteEdition(updatedOrders)}
      />
    </Stack>
  );
}

export default QClassification;
